/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import Head from 'next/head';
import { useMemo } from 'react';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { countryCodes } from '@/constants/locale';
import { HomePageTemplate } from '../components/HomePageTemplate';
import { useProductDataTopNavMenu } from '@/features/topNav/api/getProductDataTopNavMenu';
import { useProductDataTopNavMenuV2 } from '@/features/topNav/api/getProductDataTopNavMenuV2';
import { useDeviceType } from '@/utils/useDeviceType';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { type Category, type CategoryV2 } from '@/features/topNav';

type SEOLinks = {
  canonical: string;
  alternates: Array<{
    key: string;
    lang: string;
    url: string;
  }>;
};

// Simple CategoryItem component to avoid duplication
const CategoryItem: React.FC<{ category: Category | CategoryV2 }> = ({ category }) => (
  <li>
    <a href={category.seoUrl}>{category.label}</a>
  </li>
);

const L1CategoryLists = () => {
  const { acesEnabled } = useAcesFlags();
  const { data: productDataTopNavMenuV1 } = useProductDataTopNavMenu();
  const { data: productDataTopNavMenuV2 } = useProductDataTopNavMenuV2({
    isHamburgerMenu: true,
  });

  const data = acesEnabled ? productDataTopNavMenuV2 : productDataTopNavMenuV1;

  // Normalize the data structure regardless of the source
  const normalizedCategories = useMemo<ReadonlyArray<Category | CategoryV2>>(() => {
    if (!data) {
      return [] as Array<Category | CategoryV2>;
    }

    if (acesEnabled) {
      // For V2: Combine popularCategories with childCategories from rootCategories
      return [
        ...(data.popularCategories || []),
        ...(data.rootCategories || []).flatMap(
          (category) => (category.childCategories as Category[]) || [],
        ),
      ];
    } else {
      // For V1: Combine popularCategories and childCategories from all rootCategories
      return (data.rootCategories || []).flatMap((category) => [
        ...(category.popularCategories || []),
        ...(category.childCategories || []),
      ]);
    }
  }, [data, acesEnabled]);

  return (
    <div>
      <nav data-testid="l1-category-lists">
        <ul>
          {normalizedCategories.map((category) => (
            <CategoryItem key={category.NId || category.categoryIdPath} category={category} />
          ))}
        </ul>
      </nav>
    </div>
  );
};

const us = 'https://www.autozone.com';
const mx = 'https://www.autozone.com.mx';
const br = 'https://www.autozone.com.br';

const localeLinks: Record<Locale, SEOLinks | undefined> = {
  'en-US': {
    canonical: us,
    alternates: [
      {
        key: 'linkAlternateObj',
        lang: countryCodes.mx,
        url: mx,
      },
      {
        key: 'linkAlternateObj_BR',
        lang: countryCodes.ptBr,
        url: br,
      },
    ],
  },
  'es-MX': {
    canonical: mx,
    alternates: [
      {
        key: 'linkAlternateObj',
        lang: countryCodes.us,
        url: us,
      },
      {
        key: 'linkAlternateObj_BR',
        lang: countryCodes.ptBr,
        url: br,
      },
    ],
  },
  'pt-BR': {
    canonical: br,
    alternates: [
      {
        key: 'linkAlternateObj',
        lang: countryCodes.us,
        url: us,
      },
      {
        key: 'linkAlternateObj_MX',
        lang: countryCodes.mx,
        url: mx,
      },
    ],
  },
};

export const HomePage = () => {
  useAnalytics();
  const deviceType = useDeviceType();
  const locale = useLocale();

  const links = localeLinks[locale];

  // TODO: use CMS data for SEO. Similar to SB-4566

  return (
    <>
      <Head>
        {links && <link key="linkCanonical" rel="canonical" href={links.canonical} />}
        <link key="linkAlternateDefault" rel="alternate" hrefLang="x-default" href={us} />
        {links?.alternates.map((link) => (
          <link key={link.key} rel="alternate" hrefLang={link.lang} href={link.url} />
        ))}
      </Head>
      {deviceType === 'bot' ? <L1CategoryLists /> : null}
      <HomePageTemplate />
    </>
  );
};
